import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, FileText, Layers, Palette, Award, Zap } from "lucide-react";

const Feature = ({ icon: Icon, title, children }) => (
  <div className="bg-white brut-border p-6 brut-shadow">
    <Icon className="w-6 h-6 mb-4" strokeWidth={1.5} />
    <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
    <p className="text-sm text-neutral-700 leading-relaxed">{children}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen">
      {/* NAV */}
      <nav className="bg-white brut-border-b sticky top-0 z-40" style={{borderBottom:"1px solid #0A0A0A"}}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl font-black tracking-tighter" data-testid="brand-logo">RESUME</Link>
          <div className="hidden md:flex items-center gap-8 label-eyebrow">
            <a href="#features">Features</a>
            <a href="#templates">Templates</a>
            <a href="#pricing">Pricing</a>
          </div>
          <Link to="/login" className="brut-border brut-shadow bg-black text-white px-5 py-2 font-medium" data-testid="nav-signin-btn">Sign in →</Link>
        </div>
      </nav>

      {/* MARQUEE */}
      <div className="border-b bg-black text-white overflow-hidden" style={{borderBottom:"1px solid #0A0A0A"}}>
        <div className="whitespace-nowrap py-3 label-eyebrow flex gap-12" style={{animation:"marquee 30s linear infinite"}}>
          {Array.from({length: 8}).map((_,i)=>(
            <span key={i} className="flex items-center gap-12">
              <span>ATS-OPTIMIZED</span><span>·</span><span>AI-POWERED</span><span>·</span><span>PIXEL-PERFECT</span><span>·</span>
            </span>
          ))}
        </div>
        <style>{`@keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }`}</style>
      </div>

      {/* HERO — Bento tetris */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 flex flex-col justify-between bg-white brut-border p-10 brut-shadow-lg min-h-[420px]">
            <div className="label-eyebrow">A resume builder for people who ship</div>
            <div>
              <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-black leading-[0.85] tracking-tighter">
                Draft it with AI.<br/>
                <span className="text-[#0047AB]">Print it</span> like a book.
              </h1>
              <p className="mt-8 text-lg text-neutral-700 max-w-xl">Generate crisp bullet points, swap serif for sans, drop your headshot, pin certificates — export a PDF that keeps its geometry across every device.</p>
              <div className="mt-8 flex gap-4">
                <Link to="/login" data-testid="hero-cta-primary" className="brut-border brut-shadow bg-black text-white px-6 py-3 font-medium inline-flex items-center gap-2">
                  Start building free <ArrowUpRight className="w-4 h-4" />
                </Link>
                <a href="#templates" className="brut-border brut-shadow bg-white px-6 py-3 font-medium">See templates</a>
              </div>
            </div>
          </div>
          <div className="md:col-span-4 flex flex-col gap-6">
            <div className="bg-[#0047AB] text-white brut-border p-6 brut-shadow flex-1">
              <Sparkles className="w-6 h-6 mb-4" />
              <div className="font-display text-2xl leading-tight">"Draft with AI"<br/>lives on every field.</div>
              <div className="label-eyebrow mt-6 opacity-70">Powered by Claude Sonnet 5</div>
            </div>
            <div className="bg-white brut-border p-0 brut-shadow flex-1 overflow-hidden">
              <img src="https://images.pexels.com/photos/110644/pexels-photo-110644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="desk" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 items-end gap-8 mb-12">
          <h2 className="font-display text-4xl md:text-5xl uppercase font-black leading-none">Everything a resume<br/>should already do.</h2>
          <p className="text-neutral-700 md:text-right md:justify-self-end max-w-md">Not a "tool with templates". A studio: type on the left, watch A4 update on the right.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Feature icon={Sparkles} title="AI drafting">Summary, bullet points, skills, cover letter — write with Claude Sonnet 5 in one click.</Feature>
          <Feature icon={Palette} title="Type & color">Serif, sans, mono. Pick a font, tune a size, dial an accent — the preview updates as you type.</Feature>
          <Feature icon={Layers} title="Live A4 preview">See the actual printable page as you edit. No surprises at export.</Feature>
          <Feature icon={Award} title="Certificates & photos">Upload badges, headshots, and certificates. Drop them into the layout.</Feature>
          <Feature icon={FileText} title="One-click PDF">Export a print-ready PDF. Also great for LinkedIn upload.</Feature>
          <Feature icon={Zap} title="Pro themes">Unlock studio-grade themes for a one-time UPI payment. No subscriptions.</Feature>
        </div>
      </section>

      {/* TEMPLATES showcase */}
      <section id="templates" className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="label-eyebrow">Six starting points</div>
          <h2 className="font-display text-4xl md:text-5xl uppercase font-black leading-none mt-2">Templates you'd frame.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {["Brutalist Grid","Editorial Serif","Monotype Studio","Executive Deep","Compact Precision","Gazette Print"].map((n,i)=>(
            <div key={i} className="bg-white brut-border brut-shadow overflow-hidden">
              <div className="aspect-[3/4] p-6 flex flex-col justify-between" style={{background: i%2===0? "#F4F4F0":"white"}}>
                <div>
                  <div className="label-eyebrow">Template · 0{i+1}</div>
                  <div className={`mt-3 ${i===1||i===5?"font-serif-r":i===2?"font-mono-r":"font-display"} text-2xl leading-none font-black`}>Jane Draper</div>
                  <div className="text-xs mt-1 text-neutral-600">Product Designer</div>
                  <div className="mt-4 h-[1px] bg-black" />
                  <div className="mt-4 space-y-1.5">
                    {Array.from({length:6}).map((_,k)=>(
                      <div key={k} className="h-1 bg-neutral-300" style={{width: `${60+((k*13)%40)}%`}} />
                    ))}
                  </div>
                </div>
                <div className="label-eyebrow">{n}{i>=2 && <span className="ml-2 bg-[#FFD700] px-1.5 py-0.5 text-black">PRO</span>}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white brut-border p-10 brut-shadow">
            <div className="label-eyebrow">Free</div>
            <div className="mt-4 font-display text-6xl font-black">₹0</div>
            <div className="text-neutral-600 mt-2">Forever. No card.</div>
            <ul className="mt-8 space-y-3 text-sm">
              <li>✓ Two free templates</li>
              <li>✓ Unlimited resumes</li>
              <li>✓ AI drafting (Claude Sonnet 5)</li>
              <li>✓ PDF export</li>
            </ul>
            <Link to="/login" className="mt-10 block text-center brut-border brut-shadow bg-white px-6 py-3 font-medium">Start free</Link>
          </div>
          <div className="bg-[#0A0A0A] text-white brut-border p-10 brut-shadow-lg relative">
            <div className="absolute -top-3 right-6 bg-[#FFD700] text-black label-eyebrow px-3 py-1">Pro · lifetime</div>
            <div className="label-eyebrow opacity-70">Pro Studio</div>
            <div className="mt-4 font-display text-6xl font-black">₹499</div>
            <div className="text-white/60 mt-2">One-time. UPI accepted.</div>
            <ul className="mt-8 space-y-3 text-sm">
              <li>✓ All six studio templates</li>
              <li>✓ Certificate & image uploads</li>
              <li>✓ Custom colors & fonts</li>
              <li>✓ Priority AI throughput</li>
            </ul>
            <Link to="/login" className="mt-10 block text-center brut-border-2 border-white bg-white text-black px-6 py-3 font-medium hover:bg-[#FFD700]">Unlock Pro →</Link>
          </div>
        </div>
      </section>

      <footer className="border-t bg-white" style={{borderTop:"1px solid #0A0A0A"}}>
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-display text-xl font-black">RESUME</div>
          <div className="label-eyebrow text-neutral-600">© 2026 · Made for people who ship</div>
        </div>
      </footer>
    </div>
  );
}
